import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CreditCard, FileDown, Receipt, UserCog } from "lucide-react";
import { API, api, eur, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageShell } from "@/components/Shell";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { AppointmentList } from "@/components/AppointmentList";
import { RequestsList } from "@/components/panels/RequestsPanel";
import { DocumentsPanel } from "@/components/panels/DocumentsPanel";
import { StatusBadge } from "@/components/StatusBadge";
import { MyCard } from "@/components/wallet/MyCard";
import { SavedJobs } from "@/components/cafe/SavedJobs";

function useList(url) {
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get(url).then(({ data }) => setItems(data)), [url]);
  useEffect(() => {
    load();
  }, [load]);
  return [items, load];
}

function Payments() {
  const [items] = useList("/payments");
  const [rec, setRec] = useState(null);
  const navigate = useNavigate();
  if (!items.length) return <p className="text-sm text-slate-500">Nessun pagamento.</p>;
  return (
    <div className="space-y-2" data-testid="payments-list">
      <p className="text-xs font-semibold text-amber-700">Pagamenti con Stripe in modalità di prova: nessun addebito reale.</p>
      {items.map((p) => (
        <div key={p.id} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`payment-item-${p.id}`}>
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-semibold text-slate-800">{p.description} · {eur(p.amount)}</span>
            <StatusBadge status={p.status} />
          </div>
          <p className="text-xs text-slate-500">{fmtDateTime(p.created_at)} · provider {p.provider}{p.refund_mode === "stripe" ? " · rimborso accreditato sulla carta" : p.refund_mode === "wallet" ? " · rimborso riaccreditato sulla Selvix Card" : ""}</p>
          {p.payable && <Button size="sm" data-testid={`payment-pay-${p.id}`} onClick={() => navigate(`/pagamento/${p.id}`)} className="mt-1 rounded-full bg-sky-600 hover:bg-sky-700"><CreditCard className="mr-1 h-3.5 w-3.5" /> Paga</Button>}
          {p.receipt_number && <Button size="sm" variant="ghost" data-testid={`payment-receipt-${p.id}`} onClick={() => setRec(rec === p.id ? null : p.id)}><Receipt className="mr-1 h-3.5 w-3.5" /> Ricevuta</Button>}
          {p.receipt_number && <a href={`${API}/payments/${p.id}/receipt.pdf`} data-testid={`payment-receipt-pdf-${p.id}`} className="ml-1 inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-semibold text-sky-700 hover:bg-sky-50"><FileDown className="h-3.5 w-3.5" /> Scarica PDF</a>}
          {rec === p.id && (
            <div className="mt-2 rounded-lg bg-slate-50 p-3 font-mono text-xs text-slate-700" data-testid="receipt-view">
              RICEVUTA {p.receipt_number}{p.provider === "STRIPE_TEST" ? " (STRIPE – PROVA)" : p.provider === "SELVIX_WALLET" ? " (SELVIX CARD)" : " (SIMULATA)"}<br />Clinica Virtuale Doctor Selvix<br />{p.description}<br />Importo: {eur(p.amount)}<br />Pagato il: {fmtDateTime(p.paid_at)}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function Notifications() {
  const [items, load] = useList("/notifications");
  useEffect(() => {
    api.post("/notifications/read").then(() => window.dispatchEvent(new CustomEvent("selvix-notification")));
  }, []);
  return (
    <div className="space-y-2" data-testid="notifications-list">
      {!items.length && <p className="text-sm text-slate-500">Nessuna notifica.</p>}
      {items.map((n) => <div key={n.id} className="rounded-xl border border-slate-200 bg-white p-3 text-sm"><b>{n.title}</b><p className="text-xs text-slate-500">{n.body} · {fmtDateTime(n.at)}</p></div>)}
      <Button size="sm" variant="outline" onClick={load}>Aggiorna</Button>
    </div>
  );
}

export default function PatientArea() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [appts, loadAppts] = useList("/appointments/mine");
  const [reqs] = useList("/requests/mine");
  const isPro = ["medico", "infermiere"].includes(user.role);
  const asked = params.get("tab") || "carta";
  const tab = asked === "offerte" && !isPro ? "appuntamenti" : asked;
  return (
    <PageShell title={`Ciao, ${user.name?.split(" ")[0]}`} subtitle="Area personale riservata: i tuoi dati sono visibili solo a te e ai soggetti che autorizzi." testid="patient-area">
      <Link to="/profilo" data-testid="patient-profile-link" className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-sky-700 hover:underline"><UserCog className="h-4 w-4" /> Profilo, identità e sicurezza</Link>
      <Tabs value={tab} onValueChange={(t) => setParams({ tab: t })}>
        <TabsList className="mb-6 flex h-auto flex-wrap justify-start">
          {[["carta", "La mia DoctorSelvix Card"], ["appuntamenti", "Appuntamenti"], ["richieste", "Richieste mediche"], ["documenti", "Documenti"], ["pagamenti", "Pagamenti"], ...(isPro ? [["offerte", "Offerte salvate"]] : []), ["notifiche", "Notifiche"]].map(([k, l]) => <TabsTrigger key={k} value={k} data-testid={`patient-tab-${k}`}>{l}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="appuntamenti"><AppointmentList items={appts} reload={loadAppts} /></TabsContent>
        <TabsContent value="richieste"><RequestsList items={reqs} /></TabsContent>
        <TabsContent value="documenti"><div className="max-w-2xl"><DocumentsPanel /></div></TabsContent>
        <TabsContent value="pagamenti"><Payments /></TabsContent>
        <TabsContent value="carta"><MyCard /></TabsContent>
        {isPro && <TabsContent value="offerte"><SavedJobs /></TabsContent>}
        <TabsContent value="notifiche"><Notifications /></TabsContent>
      </Tabs>
    </PageShell>
  );
}
